import React from "react";
import styled from "styled-components";
import { Heading, MultiSelect, Paper, Option, Icon } from "src/components";
import { Row } from "./editData/gridElements";
import { useTranslation } from "react-i18next";
import { observer, inject } from "mobx-react";
import { ItemStores, AuthStore, ItemStore } from "src/stores";
import { DataGrid, GridActionsCellItem, GridColDef, GridActionsColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { IItem, ItemType } from "src/types";
import { Item } from "src/models";
import { EditItem } from "./editData";
import { format } from "date-fns";
import toast from "react-hot-toast";

export interface IRoomArchiveProps {
  itemStores?: ItemStores,
  authStore?:  AuthStore,
}

export const RoomArchive = inject("itemStores", "authStore")(observer((props: IRoomArchiveProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const { itemStores, authStore } = props;

  const archiveOptions = [
    { value: "question",    label: t("question_plural")},
    { value: "deliverable", label: t("deliverable_plural")},
    { value: "milestone",   label: t("milestone_plural")},
    { value: "meeting",     label: t("meeting_plural")},
  ];

  const [archiveType, setArchiveType] = React.useState<Option>(archiveOptions[0]);

  const store: ItemStore<Item> = itemStores[archiveType.value as ItemType];

  const columns: (GridColDef | GridActionsColDef)[] = [
    {
      field:             'number',
      headerName:        t('number'),
      sortable:          false,
      disableColumnMenu: true,
      headerClassName:   'columnHeader',
    },{
      field:             'title',
      headerName:        t('title'),
      sortable:          false,
      disableColumnMenu: true,
      flex:              1,
      headerClassName:   'columnHeader',
    },{
      field:             'updated_at',
      headerName:        t('archived_at'),
      sortable:          false,
      flex:              1,
      disableColumnMenu: true,
      headerClassName:   'columnHeader',
      renderCell:        (params: GridRenderCellParams<Item>) => (
        format(new Date(params.row.updated_at), 'yyyy-MM-dd HH:mm')
      ),
    },{
      field: 'action',
      type:  'actions',
      getActions: (params: GridRowParams<Item>) => [
        <GridActionsCellItem 
          icon       = {<Icon size="medium" iconId="delete"/>}
          label      = {t('delete')}
          onClick    = {async () => {
            try {
              if(!await params.row.remove()) {
                console.error("Failed to remove", archiveType.value);
                toast.error(t("failed_remove", {context: archiveType.value}))
              } else {
                toast.success(t("remove", {context: archiveType.value}))
              }
            } catch(err) {
              console.error("Failed to remove", archiveType.value, err);
              toast.error(t("failed_remove", {context: archiveType.value}))
            }
          }}
        />,
        <GridActionsCellItem 
          icon       = {<Icon size="medium" iconId="redo"/>}
          label      = {t('restore')}
          onClick    = {async () => {
            try {
              await params.row.set('archived', false, true);
              toast.success(t("restore", {context: archiveType.value}));
            } catch(err) {
              console.error("Failed to restore", archiveType.value, err);
              toast.error(t("failed_restore", {context: archiveType.value}))
            }
          }}
        />,
      ],
    },
  ];

  const rows = itemStores[archiveType.value as ItemType].archive.getArray;

  if(!authStore.getCurUser.canEdit) return null;
  
  return (
    <StyledPaper
      style         = {{ maxWidth: "120rem", marginLeft: '2rem', padding: 0 }}
      display       = "flex"
      flexDirection = "column"
      width         = "100%"
      $noShadow
    >
      <Row style={{padding: "2rem 2rem"}} justify="space-between" height="fit-content" width="100%">
        <Heading type="h3">{t("archive_page")}</Heading>
        <MultiSelect
          options  = {archiveOptions}
          selected = {[archiveType]}
          onChange = {setArchiveType}
          singleSelect
          forceSelect
        />
      </Row>
      <OuterContainer>
        <DataGrid 
          disableSelectionOnClick
          paginationMode     = "server"
          rows               = {rows}
          onRowClick         = {(params: GridRowParams) => {store.setActive(params.row)}}
          rowCount           = {store.archive.getTotal}
          columns            = {columns}
          pageSize           = {store.archivePageSize}
          rowsPerPageOptions = {[store.archivePageSize]}
          onPageChange       = {(page: number) => store.archive.goToPage(page)}
        />
      </OuterContainer>
      <EditItem />
    </StyledPaper>
  );
}));

const OuterContainer = styled.div`
  height:        80rem; 
  margin-bottom: 0rem;
  overflow:      auto;
  font-size:     24px;
`;

const StyledPaper = styled(Paper)`
  padding:       0;
  max-width:     125rem;
  overflow:      auto;
  margin-top:    2.5vh;
  margin-bottom: 2.5vh;
`;

const PageCountContainer = styled.div`
  display:         flex;
  flex-direction:  row;
  justify-content: flex-end;
  align-items:     center;
  width:           20rem;
`;

const PaginationContainer = styled.div`
  display:         flex;
  flex-direction:  row;
  align-items:     start;
  justify-content: center;
`;